/**
 * SCAN-002 — the shapes a photographed page is described by.
 *
 * Four corners, and the arithmetic that turns them into a flat rectangle. Kept
 * apart from both the detector that guesses them and the renderer that uses
 * them, because it is the part with an answer that is either right or wrong:
 * a homography either maps the corners onto the rectangle or it does not, and
 * that is provable at a desk without a camera, a canvas or a GPU.
 */

export interface Point {
  x: number;
  y: number;
}

/**
 * A page's four corners, always in this order: top-left, top-right,
 * bottom-right, bottom-left.
 *
 * The order is the whole contract. Every consumer — the overlay that draws the
 * outline, the handle the finger drags, the transform that flattens the page —
 * reads corner 0 as "top-left" and would silently produce a mirrored or
 * rotated scan if handed them in any other order. `orderQuad` is what
 * guarantees it; nothing should build one of these by hand.
 */
export type Quad = readonly [Point, Point, Point, Point];

/** Twice the signed area of the polygon, by the shoelace formula. */
function shoelace(quad: Quad): number {
  let sum = 0;
  for (let i = 0; i < 4; i += 1) {
    const a = quad[i] as Point;
    const b = quad[(i + 1) % 4] as Point;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum;
}

export const quadArea = (quad: Quad): number => Math.abs(shoelace(quad)) / 2;

/**
 * Whether the four points make a shape a page could actually be.
 *
 * A convex quadrilateral is what a rectangle photographed from any angle
 * becomes; a bow-tie is what two mis-paired corners become. The test is that
 * every turn goes the same way round.
 */
export function isConvex(quad: Quad): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const a = quad[i] as Point;
    const b = quad[(i + 1) % 4] as Point;
    const c = quad[(i + 2) % 4] as Point;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross === 0) continue;
    const current = cross > 0 ? 1 : -1;
    if (sign === 0) sign = current;
    else if (sign !== current) return false;
  }
  return sign !== 0;
}

/**
 * Four points in any order, sorted into the corner order above.
 *
 * Sorting by angle around the centre puts them in ring order, which is most of
 * the work; the remaining question is which of the four is the top-left, and
 * that is the one closest to the origin. Going round the ring from there in the
 * direction that increases y first is clockwise on a screen, whose y axis
 * points down.
 */
export function orderQuad(points: readonly Point[]): Quad {
  if (points.length !== 4) throw new Error(`orderQuad needs 4 points, got ${points.length}`);
  const cx = points.reduce((sum, p) => sum + p.x, 0) / 4;
  const cy = points.reduce((sum, p) => sum + p.y, 0) / 4;

  const ring = [...points].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));

  let start = 0;
  let best = Infinity;
  for (let i = 0; i < 4; i += 1) {
    const p = ring[i] as Point;
    const score = p.x + p.y;
    if (score < best) {
      best = score;
      start = i;
    }
  }

  const rotated = [0, 1, 2, 3].map((i) => ring[(start + i) % 4] as Point);
  // atan2 ascends counter-clockwise in maths and clockwise on a screen only
  // because y is flipped — so the ring may arrive either way round depending on
  // the sign of the area. Reversing a wrongly-wound ring keeps top-left first.
  if (shoelace(rotated as unknown as Quad) < 0) {
    return [rotated[0], rotated[3], rotated[2], rotated[1]] as unknown as Quad;
  }
  return rotated as unknown as Quad;
}

const distance = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * How large the flattened page should be.
 *
 * A photograph taken at an angle has one edge nearer the lens and therefore
 * longer, and picking either one alone would squash or stretch the text.
 * Averaging the two opposite sides is the usual compromise: no dimension is
 * exactly right, but neither is far enough out to be visible.
 *
 * `max` caps the result, because the source is a phone photograph of maybe
 * twelve megapixels and the output only ever becomes a page in a PDF.
 */
export function flattenedSize(quad: Quad, max = 2200): { width: number; height: number } {
  const [tl, tr, br, bl] = quad;
  const width = (distance(tl, tr) + distance(bl, br)) / 2;
  const height = (distance(tl, bl) + distance(tr, br)) / 2;
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Solve `A · x = b` for a small dense system, by Gaussian elimination with
 * partial pivoting. Eight unknowns is small enough that nothing cleverer earns
 * its complexity, and pivoting is what keeps a near-vertical page edge — whose
 * row starts with a value near zero — from dividing the answer into infinity.
 */
function solve(a: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i] as number]);

  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs((m[row] as number[])[col] as number) > Math.abs((m[pivot] as number[])[col] as number)) pivot = row;
    }
    const pivotRow = m[pivot] as number[];
    if (Math.abs(pivotRow[col] as number) < 1e-10) return null;
    m[pivot] = m[col] as number[];
    m[col] = pivotRow;

    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const target = m[row] as number[];
      const factor = (target[col] as number) / (pivotRow[col] as number);
      if (factor === 0) continue;
      for (let k = col; k <= n; k += 1) {
        target[k] = (target[k] as number) - factor * (pivotRow[k] as number);
      }
    }
  }

  // Gauss-Jordan leaves the matrix diagonal, so each unknown is one division.
  return m.map((row, i) => (row[n] as number) / (row[i] as number));
}

/**
 * The 3×3 that maps the flat output rectangle back onto the photographed page.
 *
 * Deliberately that direction and not the other. Flattening the page means
 * filling every pixel of the output, and the only way to fill a pixel is to ask
 * where it came from — walking forwards from the source instead leaves holes
 * wherever the perspective stretched the image, and writes the same output
 * pixel twice wherever it squashed it. So the renderer walks the output and
 * this matrix answers "which pixel of the photograph is this one?".
 *
 * Returned as nine numbers in column-major order, which is what WebGL wants for
 * a `mat3` uniform and the only reason the layout is worth remembering.
 */
export function inverseProjection(quad: Quad, width: number, height: number): Float32Array | null {
  // A quad with no area has no projection onto a rectangle, and the solver does
  // not always say so: four corners a ten-millionth of a pixel apart give a
  // system that is merely badly conditioned rather than singular, and it answers
  // with enormous finite numbers that render as a blank page. Two dragged
  // corners meeting is exactly how a person produces one.
  if (quadArea(quad) < 1) return null;

  // The output rectangle's corners, in the same order the quad's are in.
  const from: Point[] = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];

  const rows: number[][] = [];
  const values: number[] = [];
  for (let i = 0; i < 4; i += 1) {
    const { x: u, y: v } = from[i] as Point;
    const { x, y } = quad[i] as Point;
    rows.push([u, v, 1, 0, 0, 0, -u * x, -v * x]);
    values.push(x);
    rows.push([0, 0, 0, u, v, 1, -u * y, -v * y]);
    values.push(y);
  }

  const h = solve(rows, values);
  if (!h) return null;
  if (h.some((n) => !Number.isFinite(n))) return null;

  // Column-major: [m00 m10 m20, m01 m11 m21, m02 m12 m22].
  return new Float32Array([
    h[0] as number, h[3] as number, h[6] as number,
    h[1] as number, h[4] as number, h[7] as number,
    h[2] as number, h[5] as number, 1,
  ]);
}

/** Apply a column-major 3×3 projection to a point, dividing through by w. */
export function project(matrix: Float32Array, point: Point): Point {
  const w = (matrix[2] as number) * point.x + (matrix[5] as number) * point.y + (matrix[8] as number);
  return {
    x: ((matrix[0] as number) * point.x + (matrix[3] as number) * point.y + (matrix[6] as number)) / w,
    y: ((matrix[1] as number) * point.x + (matrix[4] as number) * point.y + (matrix[7] as number)) / w,
  };
}

/**
 * The page turned a quarter-turn clockwise, `steps` times.
 *
 * Turning a scan does not need a rotated image, a rotated canvas or a second
 * pass over four million pixels: it only needs the four corners renaming. Read
 * the page's bottom-left corner as its top-left and everything downstream —
 * the output's width and height, which way its rows run, where the text sits —
 * comes out turned, because all of it is derived from the corner order.
 */
export function rotateQuad(quad: Quad, steps: number): Quad {
  const turns = ((steps % 4) + 4) % 4;
  return [0, 1, 2, 3].map((i) => quad[(i + 4 - turns) % 4] as Point) as unknown as Quad;
}

/**
 * The same corners, pulled very slightly towards the middle of the page.
 *
 * The detector works on a copy of the frame 240 pixels across, so one of its
 * bins is about seven pixels of a phone photograph, and a corner it places
 * perfectly is still up to seven pixels from where the paper actually ends.
 * Half those errors fall outside the page, and outside the page is the desk —
 * which arrives in the scan as a dark rim down one or two edges, on every page,
 * because the error is quantisation and not noise and so does not average out.
 *
 * Measured before this existed: 43% of the border pixels of a straightened page
 * were desk rather than paper.
 *
 * Pulling in by a hundred and twenty-fifth of the page covers about two of the
 * detector's bins. On an A4 sheet that is under two millimetres, inside the
 * margin of any document that has one, and nothing is lost that was not
 * already going to be a black line.
 */
export function insetQuad(quad: Quad, fraction = 0.008): Quad {
  const cx = quad.reduce((sum, p) => sum + p.x, 0) / 4;
  const cy = quad.reduce((sum, p) => sum + p.y, 0) / 4;
  return quad.map((p) => ({
    x: p.x + (cx - p.x) * fraction,
    y: p.y + (cy - p.y) * fraction,
  })) as unknown as Quad;
}

/** The same quad measured against a differently-sized copy of the same image. */
export const scaleQuad = (quad: Quad, sx: number, sy: number): Quad =>
  quad.map((p) => ({ x: p.x * sx, y: p.y * sy })) as unknown as Quad;

/** The whole frame, as the quad to fall back on when nothing is detected. */
export const fullFrame = (width: number, height: number): Quad => [
  { x: 0, y: 0 },
  { x: width, y: 0 },
  { x: width, y: height },
  { x: 0, y: height },
];

/**
 * How far apart two candidate outlines are, as a fraction of the frame.
 *
 * This is what "the camera is being held still" means in practice: the same
 * page detected two frames running, with every corner within a hair of where it
 * was. Measured per corner and taken at its worst rather than averaged, because
 * one corner sliding while the other three sit still is exactly the case — a
 * hand rotating slowly — that an average would call steady.
 */
export function quadDrift(a: Quad, b: Quad, frameSize: number): number {
  let worst = 0;
  for (let i = 0; i < 4; i += 1) {
    worst = Math.max(worst, distance(a[i] as Point, b[i] as Point));
  }
  return worst / frameSize;
}
